import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { useMemo } from 'react';

/** Built-in tones. Texture darkens via `background-blend-mode: multiply`. */
export type PaperTone =
  | 'cream'
  | 'manila'
  | 'kraft'
  | 'postit-yellow'
  | 'postit-pink'
  | 'postit-blue'
  | 'postit-green';

const TONE_COLORS: Record<PaperTone, string> = {
  cream: 'var(--color-paper-cream)',
  manila: 'var(--color-paper-manila)',
  kraft: 'var(--color-paper-kraft)',
  'postit-yellow': 'var(--color-postit-yellow)',
  'postit-pink': 'var(--color-postit-pink)',
  'postit-blue': 'var(--color-postit-blue)',
  'postit-green': 'var(--color-postit-green)',
};

// 12 stable crops; different index per instance keeps adjacent sheets distinct.
const CROPS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 12, y: 18 },
  { x: 70, y: 35 },
  { x: 28, y: 80 },
  { x: 85, y: 65 },
  { x: 5, y: 50 },
  { x: 55, y: 5 },
  { x: 92, y: 22 },
  { x: 38, y: 60 },
  { x: 18, y: 92 },
  { x: 75, y: 88 },
  { x: 48, y: 28 },
  { x: 8, y: 70 },
];

/** djb2 hash for stable crop pick from a string seed. */
const hashSeed = (s: string): number => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return h >>> 0;
};

const cropFor = (seed: string | number | undefined): { x: number; y: number } => {
  if (seed === undefined) {
    return CROPS[Math.floor(Math.random() * CROPS.length)]!;
  }
  const n = typeof seed === 'number' ? seed : hashSeed(seed);
  return CROPS[n % CROPS.length]!;
};

export type PaperSheetProps = HTMLAttributes<HTMLDivElement> & {
  /** Built-in tone. Ignored when `color` is set. */
  tone?: PaperTone;
  /** Custom base color; wins over `tone`. */
  color?: string;
  /** Stable seed for crop. Omit for random crop on mount. */
  seed?: string | number;
  children?: ReactNode;
};

/** Paper-textured container backed by the `.paper-sheet` CSS utility. */
export const PaperSheet = ({
  tone = 'cream',
  color,
  seed,
  className,
  style,
  children,
  ...rest
}: PaperSheetProps) => {
  const crop = useMemo(() => cropFor(seed), [seed]);
  const base = color ?? TONE_COLORS[tone];

  return (
    <div
      {...rest}
      className={`paper-sheet ${className ?? ''}`}
      style={
        {
          ...style,
          '--paper-color': base,
          '--paper-pos-x': `${crop.x}%`,
          '--paper-pos-y': `${crop.y}%`,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
};
